import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import { AppHeader } from '../../shared/components/AppComponents';
import './TravelApp.css';

const tripStatusClasses = { 0: 'planning', 1: 'active', 2: 'completed', 3: 'cancelled' };
const luggageTypeIcons = { 0: '🧳', 1: '🎒' };
const packingCategories = ['Clothes', 'Toiletries', 'Electronics', 'Documents', 'Medicine', 'Entertainment', 'Other'];

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : '';
const formatDateTime = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : '';

function TravelApp({ user }) {
  const { instance } = useMsal();
  const { t } = useTranslation();

  const tripStatusLabels = { 0: t('travel.status.planning'), 1: t('travel.status.active'), 2: t('travel.status.completed'), 3: t('travel.status.cancelled') };
  const reservationTypeLabels = { 0: t('travel.reservations.restaurant'), 1: t('travel.reservations.tour'), 2: t('travel.reservations.event'), 3: t('travel.reservations.carRental'), 4: t('travel.reservations.train'), 5: t('travel.reservations.ferry'), 6: t('travel.reservations.other') };
  const documentTypeLabels = { 0: t('travel.documents.passport'), 1: t('travel.documents.visa'), 2: t('travel.documents.insurance'), 3: t('travel.documents.vaccination'), 4: t('travel.documents.ticket'), 5: t('travel.documents.other') };
  const contactCategoryLabels = { 0: t('travel.contacts.emergency'), 1: t('travel.contacts.hotel'), 2: t('travel.contacts.embassy'), 3: t('travel.contacts.guide'), 4: t('travel.contacts.airline'), 5: t('travel.contacts.other') };
  const noteCategoryLabels = { 0: t('travel.notes.weather'), 1: t('travel.notes.currency'), 2: t('travel.notes.timeZone'), 3: t('travel.notes.culture'), 4: t('travel.notes.language'), 5: t('travel.notes.general') };
  const itineraryCategoryLabels = { 0: t('travel.itinerary.activity'), 1: t('travel.itinerary.dining'), 2: t('travel.itinerary.transport'), 3: t('travel.itinerary.sightseeing'), 4: t('travel.itinerary.other') };

  const tabs = [
    { id: 'overview', icon: '📋', label: t('travel.tabs.overview') },
    { id: 'todos', icon: '✅', label: t('travel.tabs.todos') },
    { id: 'packing', icon: '🧳', label: t('travel.tabs.packing') },
    { id: 'flights', icon: '✈️', label: t('travel.tabs.flights') },
    { id: 'accommodations', icon: '🏨', label: t('travel.tabs.accommodations') },
    { id: 'itinerary', icon: '📅', label: t('travel.tabs.itinerary') },
    { id: 'reservations', icon: '🎫', label: t('travel.tabs.reservations') },
    { id: 'budget', icon: '💰', label: t('travel.tabs.budget') },
    { id: 'documents', icon: '📄', label: t('travel.tabs.documents') },
    { id: 'contacts', icon: '📞', label: t('travel.tabs.contacts') },
    { id: 'notes', icon: '📝', label: t('travel.tabs.notes') },
  ];
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState(null);
  const [showCreateTrip, setShowCreateTrip] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({});

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadTrips = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiFetchJson(instance, '/api/travel');
      setTrips(unwrap(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  const loadTripDetail = useCallback(async (id) => {
    try {
      setError('');
      const data = await apiFetchJson(instance, `/api/travel/${id}`);
      setSelectedTrip(data);
    } catch (err) {
      setError(err.message);
    }
  }, [instance]);

  useEffect(() => { loadTrips(); }, [loadTrips]);

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    try {
      await apiFetchJson(instance, '/api/travel', {
        method: 'POST',
        body: JSON.stringify(newItem),
      });
      setShowCreateTrip(false);
      setNewItem({});
      showMessage(t('travel.tripCreated'));
      loadTrips();
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const handleDeleteTrip = async (id) => {
    if (!window.confirm(t('travel.trip.deleteConfirm'))) return;
    try {
      await apiFetchJson(instance, `/api/travel/${id}`, { method: 'DELETE' });
      showMessage(t('travel.tripDeleted'));
      setSelectedTrip(null);
      loadTrips();
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const apiCall = async (endpoint, method, body) => {
    await apiFetchJson(instance, endpoint, {
      method,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  };

  const handleAddItem = async (e, section) => {
    e.preventDefault();
    try {
      await apiCall(`/api/travel/${selectedTrip.id}/${section}`, 'POST', newItem);
      setNewItem({});
      showMessage(t('travel.itemAdded'));
      loadTripDetail(selectedTrip.id);
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const handleUpdateItem = async (e, section, itemId) => {
    e.preventDefault();
    try {
      await apiCall(`/api/travel/${selectedTrip.id}/${section}/${itemId}`, 'PUT', editingItem);
      setEditingItem(null);
      showMessage(t('travel.itemUpdated'));
      loadTripDetail(selectedTrip.id);
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const handleDeleteItem = async (section, itemId) => {
    if (!window.confirm(t('travel.deleteItemConfirm'))) return;
    try {
      await apiCall(`/api/travel/${selectedTrip.id}/${section}/${itemId}`, 'DELETE');
      showMessage(t('travel.itemDeleted'));
      loadTripDetail(selectedTrip.id);
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const handleToggle = async (section, itemId, field, currentValue) => {
    try {
      await apiCall(`/api/travel/${selectedTrip.id}/${section}/${itemId}`, 'PUT', { [field]: !currentValue });
      loadTripDetail(selectedTrip.id);
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const openTrip = (trip) => {
    setSelectedTrip(trip);
    setActiveTab('overview');
    loadTripDetail(trip.id);
  };

  const getProgress = (items, field) => {
    const list = unwrap(items);
    if (list.length === 0) return { done: 0, total: 0, pct: 0 };
    const done = list.filter(i => i[field]).length;
    return { done, total: list.length, pct: Math.round((done / list.length) * 100) };
  };

  // --- Render: Trip List ---
  const renderTripList = () => (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>{t('travel.myTrips')}</h2>
        <button className="travel-btn primary" onClick={() => setShowCreateTrip(true)}>+ {t('travel.newTrip')}</button>
      </div>

      {showCreateTrip && (
        <form className="travel-form" onSubmit={handleCreateTrip}>
          <div className="travel-form-row">
            <input placeholder={t('travel.trip.name')} value={newItem.name || ''} onChange={e => setNewItem({ ...newItem, name: e.target.value })} required />
            <input placeholder={t('travel.trip.destination')} value={newItem.destination || ''} onChange={e => setNewItem({ ...newItem, destination: e.target.value })} required />
          </div>
          <div className="travel-form-row">
            <input type="date" value={newItem.startDate || ''} onChange={e => setNewItem({ ...newItem, startDate: e.target.value })} required />
            <input type="date" value={newItem.endDate || ''} onChange={e => setNewItem({ ...newItem, endDate: e.target.value })} required />
          </div>
          <div className="travel-form-row">
            <textarea placeholder={t('travel.descriptionOptional')} value={newItem.description || ''} onChange={e => setNewItem({ ...newItem, description: e.target.value })} />
          </div>
          <div className="travel-form-actions">
            <button type="submit" className="travel-btn primary">{t('travel.create')}</button>
            <button type="button" className="travel-btn secondary" onClick={() => { setShowCreateTrip(false); setNewItem({}); }}>{t('travel.common.cancel')}</button>
          </div>
        </form>
      )}

      {trips.length === 0 ? (
        <div className="travel-empty">{t('travel.noTrips')} ✈️</div>
      ) : (
        <div className="travel-trips-grid">
          {trips.map(trip => {
            const packing = getProgress(trip.packingItems, 'isPacked');
            const todos = getProgress(trip.todos, 'isCompleted');
            return (
              <div key={trip.id} className="travel-trip-card" onClick={() => openTrip(trip)}>
                <div className="travel-card-header">
                  <div>
                    <h3>{trip.name}</h3>
                    <div className="travel-destination">📍 {trip.destination}</div>
                  </div>
                  <span className={`travel-status-badge ${tripStatusClasses[trip.status] || 'planning'}`}>
                    {tripStatusLabels[trip.status] || 'Planning'}
                  </span>
                </div>
                <div className="travel-dates">
                  {formatDate(trip.startDate)} — {formatDate(trip.endDate)}
                  {trip.createdByName && <span className="travel-created-by">👤 {trip.createdByName}</span>}
                </div>
                <div className="travel-card-progress">
                  {packing.total > 0 && (
                    <div>
                      <div className="travel-progress-label"><span>🧳 {t('travel.packing.progressLabel')}</span><span>{packing.done}/{packing.total}</span></div>
                      <div className="travel-progress-bar"><div className="travel-progress-fill" style={{ width: `${packing.pct}%` }} /></div>
                    </div>
                  )}
                  {todos.total > 0 && (
                    <div>
                      <div className="travel-progress-label"><span>✅ {t('travel.todos.progressLabel')}</span><span>{todos.done}/{todos.total}</span></div>
                      <div className="travel-progress-bar"><div className="travel-progress-fill" style={{ width: `${todos.pct}%` }} /></div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // --- Render: Overview Tab ---
  const renderOverview = () => {
    const trip = selectedTrip;
    const packing = getProgress(trip.packingItems, 'isPacked');
    const todos = getProgress(trip.todos, 'isCompleted');
    const flights = unwrap(trip.flights);
    const accommodations = unwrap(trip.accommodations);
    const budgetItems = unwrap(trip.budgetItems);
    const totalEstimated = budgetItems.reduce((s, b) => s + (b.estimatedAmount || 0), 0);
    const totalActual = budgetItems.reduce((s, b) => s + (b.actualAmount || 0), 0);

    return (
      <div>
        <div className="travel-overview-stats">
          <div className="travel-stat-card"><div className="stat-value">{packing.total > 0 ? `${packing.pct}%` : '—'}</div><div className="stat-label">{t('travel.overview.packed')}</div></div>
          <div className="travel-stat-card"><div className="stat-value">{todos.total > 0 ? `${todos.pct}%` : '—'}</div><div className="stat-label">{t('travel.overview.preTripDone')}</div></div>
          <div className="travel-stat-card"><div className="stat-value">{flights.length}</div><div className="stat-label">{t('travel.overview.flights')}</div></div>
          <div className="travel-stat-card"><div className="stat-value">{accommodations.length}</div><div className="stat-label">{t('travel.overview.stays')}</div></div>
          <div className="travel-stat-card"><div className="stat-value">{totalEstimated > 0 ? `${totalEstimated.toLocaleString()}` : '—'}</div><div className="stat-label">{t('travel.overview.estBudget')}</div></div>
          <div className="travel-stat-card"><div className="stat-value">{totalActual > 0 ? `${totalActual.toLocaleString()}` : '—'}</div><div className="stat-label">{t('travel.overview.actualSpent')}</div></div>
        </div>
        {trip.description && <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>{trip.description}</p>}
        <div className="travel-item-card">
          <div className="travel-item-detail">📍 <strong>{t('travel.overview.destination')}</strong> {trip.destination}</div>
          <div className="travel-item-detail">📅 <strong>{t('travel.overview.dates')}</strong> {formatDate(trip.startDate)} — {formatDate(trip.endDate)}</div>
          <div className="travel-item-detail">📊 <strong>{t('travel.overview.status')}</strong> {tripStatusLabels[trip.status] || t('travel.status.planning')}</div>
          {trip.createdByName && <div className="travel-item-detail">👤 <strong>{t('travel.overview.createdBy')}</strong> {trip.createdByName}</div>}
        </div>
      </div>
    );
  };

  // --- Render: Todos Tab ---
  const renderTodos = () => {
    const items = unwrap(selectedTrip.todos);
    return (
      <div className="travel-section">
        <div className="travel-section-header">
          <h3>✅ {t('travel.todos.title')}</h3>
          <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'todos' })}>+ {t('travel.common.add')}</button>
        </div>
        {newItem._adding === 'todos' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'todos')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.todos.taskTitle')} value={newItem.title || ''} onChange={e => setNewItem({ ...newItem, title: e.target.value })} required />
            </div>
            <div className="travel-form-row">
              <input placeholder={t('travel.todos.notesOptional')} value={newItem.description || ''} onChange={e => setNewItem({ ...newItem, description: e.target.value })} />
              <input type="date" placeholder={t('travel.todos.dueDate')} value={newItem.dueDate || ''} onChange={e => setNewItem({ ...newItem, dueDate: e.target.value })} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}
        {items.length === 0 ? <div className="travel-empty">{t('travel.todos.noTasks')}</div> : items.map(item => (
          <div key={item.id} className="travel-item-card">
            {editingItem?.id === item.id && editingItem?._section === 'todos' ? (
              <form onSubmit={e => handleUpdateItem(e, 'todos', item.id)}>
                <div className="travel-form-row">
                  <input value={editingItem.title || ''} onChange={e => setEditingItem({ ...editingItem, title: e.target.value })} required />
                  <input value={editingItem.description || ''} onChange={e => setEditingItem({ ...editingItem, description: e.target.value })} placeholder={t('travel.common.notes')} />
                </div>
                <div className="travel-form-actions">
                  <button type="submit" className="travel-btn primary small">{t('travel.common.save')}</button>
                  <button type="button" className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
                </div>
              </form>
            ) : (
              <div className="travel-item-header">
                <label className={`travel-checkbox ${item.isCompleted ? 'checked' : ''}`}>
                  <input type="checkbox" checked={item.isCompleted || false} onChange={() => handleToggle('todos', item.id, 'isCompleted', item.isCompleted)} />
                  <span>{item.title}</span>
                </label>
                <div className="travel-item-actions">
                  <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'todos' })}>✏️</button>
                  <button className="travel-btn danger small" onClick={() => handleDeleteItem('todos', item.id)}>🗑️</button>
                </div>
              </div>
            )}
            {item.description && !editingItem?.id === item.id && <div className="travel-item-detail">{item.description}</div>}
            {item.dueDate && <div className="travel-item-detail">{t('travel.todos.due')} {formatDate(item.dueDate)}</div>}
          </div>
        ))}
      </div>
    );
  };

  // --- Render: Packing Item (helper) ---
  const renderPackingItem = (item) => {
    const luggages = unwrap(selectedTrip.luggages);
    return (
      <div key={item.id} className="travel-item-card" style={{ padding: '0.5rem 1rem' }}>
        {editingItem?.id === item.id && editingItem?._section === 'packing' ? (
          <form onSubmit={e => handleUpdateItem(e, 'packing', item.id)} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input value={editingItem.name || ''} onChange={e => setEditingItem({ ...editingItem, name: e.target.value })} required style={{ flex: 1 }} />
            <select value={editingItem.category || 'Other'} onChange={e => setEditingItem({ ...editingItem, category: e.target.value })}>
              {packingCategories.map(c => <option key={c} value={c}>{t('travel.packing.categories.' + c.toLowerCase())}</option>)}
            </select>
            <select value={editingItem.luggageId || ''} onChange={e => setEditingItem({ ...editingItem, luggageId: e.target.value || null })}>
              <option value="">{t('travel.packing.unassigned')}</option>
              {luggages.map(l => <option key={l.id} value={l.id}>{luggageTypeIcons[l.type] || '🧳'} {l.name}</option>)}
            </select>
            <button type="submit" className="travel-btn primary small">{t('travel.common.save')}</button>
            <button type="button" className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
          </form>
        ) : (
          <div className="travel-item-header">
            <label className={`travel-checkbox ${item.isPacked ? 'checked' : ''}`}>
              <input type="checkbox" checked={item.isPacked || false} onChange={() => handleToggle('packing', item.id, 'isPacked', item.isPacked)} />
              <span>{item.name} {item.quantity > 1 && `(×${item.quantity})`}</span>
            </label>
            <div className="travel-item-actions">
              <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'packing' })}>✏️</button>
              <button className="travel-btn danger small" onClick={() => handleDeleteItem('packing', item.id)}>🗑️</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- Render: Packing Tab ---
  const renderPacking = () => {
    const items = unwrap(selectedTrip.packingItems);
    const luggages = unwrap(selectedTrip.luggages);
    const progress = getProgress(selectedTrip.packingItems, 'isPacked');

    // Group items by luggage
    const itemsByLuggage = {};
    const unassigned = [];
    items.forEach(item => {
      if (item.luggageId) {
        if (!itemsByLuggage[item.luggageId]) itemsByLuggage[item.luggageId] = [];
        itemsByLuggage[item.luggageId].push(item);
      } else {
        unassigned.push(item);
      }
    });

    return (
      <div className="travel-section">
        {/* Header with progress */}
        <div className="travel-section-header">
          <h3>🧳 {t('travel.packing.title')} {progress.total > 0 && <span style={{ fontWeight: 400, fontSize: '0.85rem', color: 'var(--text-muted)' }}>({progress.done}/{progress.total})</span>}</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="travel-btn secondary small" onClick={() => setNewItem({ _adding: 'luggage', type: 0 })}>+ {t('travel.packing.addLuggage')}</button>
            <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'packing', category: 'Clothes' })}>+ {t('travel.packing.addItem')}</button>
          </div>
        </div>

        {/* Progress bar */}
        {progress.total > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <div className="travel-progress-bar"><div className="travel-progress-fill" style={{ width: `${progress.pct}%` }} /></div>
          </div>
        )}

        {/* Add luggage form */}
        {newItem._adding === 'luggage' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'luggage')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.packing.luggageNamePlaceholder')} value={newItem.name || ''} onChange={e => setNewItem({ ...newItem, name: e.target.value })} required />
              <select value={newItem.type ?? 0} onChange={e => setNewItem({ ...newItem, type: parseInt(e.target.value) })}>
                <option value={0}>🧳 {t('travel.packing.checked')}</option>
                <option value={1}>🎒 {t('travel.packing.handLuggage')}</option>
              </select>
              <input type="number" placeholder={t('travel.packing.weightLimit')} step="0.1" min="0" value={newItem.weightLimit || ''} onChange={e => setNewItem({ ...newItem, weightLimit: parseFloat(e.target.value) || null })} style={{ maxWidth: '130px' }} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}

        {/* Add packing item form */}
        {newItem._adding === 'packing' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'packing')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.packing.itemName')} value={newItem.name || ''} onChange={e => setNewItem({ ...newItem, name: e.target.value })} required />
              <select value={newItem.category || 'Clothes'} onChange={e => setNewItem({ ...newItem, category: e.target.value })}>
                {packingCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="number" placeholder={t('travel.packing.quantity')} min="1" value={newItem.quantity || ''} onChange={e => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })} style={{ maxWidth: '70px' }} />
            </div>
            <div className="travel-form-row">
              <select value={newItem.luggageId || ''} onChange={e => setNewItem({ ...newItem, luggageId: e.target.value || null })}>
                <option value="">{t('travel.packing.noLuggage')}</option>
                {luggages.map(l => <option key={l.id} value={l.id}>{luggageTypeIcons[l.type] || '🧳'} {l.name}</option>)}
              </select>
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}

        {/* Luggage bags */}
        {luggages.length > 0 && luggages.map(luggage => {
          const bagItems = itemsByLuggage[luggage.id] || [];
          const bagProgress = bagItems.length > 0 ? { done: bagItems.filter(i => i.isPacked).length, total: bagItems.length } : { done: 0, total: 0 };
          return (
            <div key={luggage.id} className="travel-packing-group">
              <div className="travel-item-header" style={{ marginBottom: '0.5rem' }}>
                <h4>{luggageTypeIcons[luggage.type] || '🧳'} {luggage.name} {bagProgress.total > 0 && <span style={{ fontWeight: 400, fontSize: '0.8rem', color: 'var(--text-muted)' }}>({bagProgress.done}/{bagProgress.total})</span>} {luggage.weightLimit && <span style={{ fontWeight: 400, fontSize: '0.75rem', color: 'var(--text-muted)' }}>• max {luggage.weightLimit}kg</span>}</h4>
                <div className="travel-item-actions">
                  <button className="travel-btn danger small" onClick={() => handleDeleteItem('luggage', luggage.id)}>🗑️</button>
                </div>
              </div>
              {bagItems.length === 0 ? (
                <div className="travel-empty" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}>{t('travel.packing.noItemsInBag')}</div>
              ) : bagItems.map(item => renderPackingItem(item))}
            </div>
          );
        })}

        {/* Unassigned items */}
        {unassigned.length > 0 && (
          <div className="travel-packing-group">
            <h4>📦 {t('travel.packing.unassignedItems')}</h4>
            {unassigned.map(item => renderPackingItem(item))}
          </div>
        )}

        {items.length === 0 && luggages.length === 0 && (
          <div className="travel-empty">{t('travel.packing.noItems')} 🧳</div>
        )}
      </div>
    );
  };

  // --- Render: Flights Tab ---
  const renderFlights = () => {
    const items = unwrap(selectedTrip.flights);
    return (
      <div className="travel-section">
        <div className="travel-section-header">
          <h3>✈️ {t('travel.flights.title')}</h3>
          <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'flights' })}>+ {t('travel.common.add')}</button>
        </div>
        {newItem._adding === 'flights' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'flights')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.flights.airline')} value={newItem.airline || ''} onChange={e => setNewItem({ ...newItem, airline: e.target.value })} required />
              <input placeholder={t('travel.flights.flightNumber')} value={newItem.flightNumber || ''} onChange={e => setNewItem({ ...newItem, flightNumber: e.target.value })} />
            </div>
            <div className="travel-form-row">
              <input placeholder={t('travel.flights.departureAirport')} value={newItem.departureAirport || ''} onChange={e => setNewItem({ ...newItem, departureAirport: e.target.value })} required />
              <input placeholder={t('travel.flights.arrivalAirport')} value={newItem.arrivalAirport || ''} onChange={e => setNewItem({ ...newItem, arrivalAirport: e.target.value })} required />
            </div>
            <div className="travel-form-row">
              <input type="datetime-local" value={newItem.departureTime || ''} onChange={e => setNewItem({ ...newItem, departureTime: e.target.value })} required />
              <input type="datetime-local" value={newItem.arrivalTime || ''} onChange={e => setNewItem({ ...newItem, arrivalTime: e.target.value })} required />
            </div>
            <div className="travel-form-row">
              <input placeholder={t('travel.flights.bookingRef')} value={newItem.bookingReference || ''} onChange={e => setNewItem({ ...newItem, bookingReference: e.target.value })} />
              <input placeholder={t('travel.flights.seat')} value={newItem.seatInfo || ''} onChange={e => setNewItem({ ...newItem, seatInfo: e.target.value })} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}
        {items.length === 0 ? <div className="travel-empty">{t('travel.flights.noFlights')}</div> : items.map(item => (
          <div key={item.id} className="travel-item-card">
            {editingItem?.id === item.id && editingItem?._section === 'flights' ? (
              <form onSubmit={e => handleUpdateItem(e, 'flights', item.id)}>
                <div className="travel-form-row">
                  <input value={editingItem.airline || ''} onChange={e => setEditingItem({ ...editingItem, airline: e.target.value })} required placeholder={t('travel.flights.airline')} />
                  <input value={editingItem.flightNumber || ''} onChange={e => setEditingItem({ ...editingItem, flightNumber: e.target.value })} placeholder={t('travel.flights.flightNumber')} />
                </div>
                <div className="travel-form-row">
                  <input value={editingItem.departureAirport || ''} onChange={e => setEditingItem({ ...editingItem, departureAirport: e.target.value })} placeholder={t('travel.flights.from')} />
                  <input value={editingItem.arrivalAirport || ''} onChange={e => setEditingItem({ ...editingItem, arrivalAirport: e.target.value })} placeholder={t('travel.flights.to')} />
                </div>
                <div className="travel-form-row">
                  <input type="datetime-local" value={editingItem.departureTime || ''} onChange={e => setEditingItem({ ...editingItem, departureTime: e.target.value })} />
                  <input type="datetime-local" value={editingItem.arrivalTime || ''} onChange={e => setEditingItem({ ...editingItem, arrivalTime: e.target.value })} />
                </div>
                <div className="travel-form-row">
                  <input value={editingItem.bookingReference || ''} onChange={e => setEditingItem({ ...editingItem, bookingReference: e.target.value })} placeholder={t('travel.flights.bookingLabel')} />
                  <input value={editingItem.seatInfo || ''} onChange={e => setEditingItem({ ...editingItem, seatInfo: e.target.value })} placeholder={t('travel.flights.seat')} />
                </div>
                <div className="travel-form-actions">
                  <button type="submit" className="travel-btn primary small">{t('travel.common.save')}</button>
                  <button type="button" className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
                </div>
              </form>
            ) : (
              <>
                <div className="travel-item-header">
                  <div>
                    <div className="travel-flight-route">
                      <span>{item.departureAirport}</span>
                      <span className="route-arrow">→</span>
                      <span>{item.arrivalAirport}</span>
                    </div>
                    <div className="travel-item-detail">{item.airline} {item.flightNumber && `• ${item.flightNumber}`}</div>
                  </div>
                  <div className="travel-item-actions">
                    <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'flights' })}>✏️</button>
                    <button className="travel-btn danger small" onClick={() => handleDeleteItem('flights', item.id)}>🗑️</button>
                  </div>
                </div>
                <div className="travel-item-detail">🛫 {formatDateTime(item.departureTime)} → 🛬 {formatDateTime(item.arrivalTime)}</div>
                {item.bookingReference && <div className="travel-item-detail">{t('travel.flights.bookingLabel')} {item.bookingReference}</div>}
                {item.seatInfo && <div className="travel-item-detail">{t('travel.flights.seatLabel')} {item.seatInfo}</div>}
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // --- Render: Accommodations Tab ---
  const renderAccommodations = () => {
    const items = unwrap(selectedTrip.accommodations);
    return (
      <div className="travel-section">
        <div className="travel-section-header">
          <h3>🏨 {t('travel.accommodations.title')}</h3>
          <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'accommodations' })}>+ {t('travel.common.add')}</button>
        </div>
        {newItem._adding === 'accommodations' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'accommodations')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.accommodations.namePlaceholder')} value={newItem.name || ''} onChange={e => setNewItem({ ...newItem, name: e.target.value })} required />
              <input placeholder={t('travel.accommodations.address')} value={newItem.address || ''} onChange={e => setNewItem({ ...newItem, address: e.target.value })} />
            </div>
            <div className="travel-form-row">
              <input type="date" value={newItem.checkInDate || ''} onChange={e => setNewItem({ ...newItem, checkInDate: e.target.value })} required />
              <input type="date" value={newItem.checkOutDate || ''} onChange={e => setNewItem({ ...newItem, checkOutDate: e.target.value })} required />
            </div>
            <div className="travel-form-row">
              <input placeholder={t('travel.accommodations.confirmation')} value={newItem.confirmationNumber || ''} onChange={e => setNewItem({ ...newItem, confirmationNumber: e.target.value })} />
              <input placeholder={t('travel.contacts.phone')} value={newItem.contactPhone || ''} onChange={e => setNewItem({ ...newItem, contactPhone: e.target.value })} />
            </div>
            <div className="travel-form-row">
              <textarea placeholder={t('travel.common.notes')} value={newItem.notes || ''} onChange={e => setNewItem({ ...newItem, notes: e.target.value })} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}
        {items.length === 0 ? <div className="travel-empty">{t('travel.accommodations.noAccommodations')}</div> : items.map(item => (
          <div key={item.id} className="travel-item-card">
            {editingItem?.id === item.id && editingItem?._section === 'accommodations' ? (
              <form onSubmit={e => handleUpdateItem(e, 'accommodations', item.id)}>
                <div className="travel-form-row">
                  <input value={editingItem.name || ''} onChange={e => setEditingItem({ ...editingItem, name: e.target.value })} required placeholder={t('travel.accommodations.name')} />
                  <input value={editingItem.address || ''} onChange={e => setEditingItem({ ...editingItem, address: e.target.value })} placeholder={t('travel.accommodations.address')} />
                </div>
                <div className="travel-form-row">
                  <input type="date" value={editingItem.checkInDate || ''} onChange={e => setEditingItem({ ...editingItem, checkInDate: e.target.value })} />
                  <input type="date" value={editingItem.checkOutDate || ''} onChange={e => setEditingItem({ ...editingItem, checkOutDate: e.target.value })} />
                </div>
                <div className="travel-form-row">
                  <input value={editingItem.confirmationNumber || ''} onChange={e => setEditingItem({ ...editingItem, confirmationNumber: e.target.value })} placeholder={t('travel.accommodations.confirmation')} />
                  <input value={editingItem.contactPhone || ''} onChange={e => setEditingItem({ ...editingItem, contactPhone: e.target.value })} placeholder={t('travel.contacts.phone')} />
                </div>
                <div className="travel-form-actions">
                  <button type="submit" className="travel-btn primary small">{t('travel.common.save')}</button>
                  <button type="button" className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
                </div>
              </form>
            ) : (
              <>
                <div className="travel-item-header">
                  <h4>{item.name}</h4>
                  <div className="travel-item-actions">
                    <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'accommodations' })}>✏️</button>
                    <button className="travel-btn danger small" onClick={() => handleDeleteItem('accommodations', item.id)}>🗑️</button>
                  </div>
                </div>
                {item.address && <div className="travel-item-detail">📍 {item.address}</div>}
                <div className="travel-item-detail">📅 {formatDate(item.checkInDate)} — {formatDate(item.checkOutDate)}</div>
                {item.confirmationNumber && <div className="travel-item-detail">{t('travel.accommodations.confirmationLabel')} {item.confirmationNumber}</div>}
                {item.contactPhone && <div className="travel-item-detail">📞 {item.contactPhone}</div>}
                {item.notes && <div className="travel-item-detail">{item.notes}</div>}
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // --- Render: Itinerary Tab ---
  const renderItinerary = () => {
    const items = unwrap(selectedTrip.itineraryItems);
    const grouped = {};
    items.forEach(item => {
      const day = item.date ? new Date(item.date).toLocaleDateString() : 'Unscheduled';
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(item);
    });

    return (
      <div className="travel-section">
        <div className="travel-section-header">
          <h3>📅 {t('travel.itinerary.title')}</h3>
          <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'itinerary', category: 0 })}>+ {t('travel.common.add')}</button>
        </div>
        {newItem._adding === 'itinerary' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'itinerary')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.itinerary.activityTitle')} value={newItem.title || ''} onChange={e => setNewItem({ ...newItem, title: e.target.value })} required />
              <select value={newItem.category ?? 0} onChange={e => setNewItem({ ...newItem, category: parseInt(e.target.value) })}>
                {Object.entries(itineraryCategoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="travel-form-row">
              <input type="date" value={newItem.date || ''} onChange={e => setNewItem({ ...newItem, date: e.target.value })} required />
              <input type="time" value={newItem.startTime || ''} onChange={e => setNewItem({ ...newItem, startTime: e.target.value })} />
              <input type="time" value={newItem.endTime || ''} onChange={e => setNewItem({ ...newItem, endTime: e.target.value })} />
            </div>
            <div className="travel-form-row">
              <input placeholder={t('travel.itinerary.location')} value={newItem.location || ''} onChange={e => setNewItem({ ...newItem, location: e.target.value })} />
              <textarea placeholder={t('travel.itinerary.description')} value={newItem.description || ''} onChange={e => setNewItem({ ...newItem, description: e.target.value })} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}
        {items.length === 0 ? <div className="travel-empty">{t('travel.itinerary.noItems')}</div> : (
          Object.entries(grouped).map(([day, dayItems]) => (
            <div key={day} className="travel-itinerary-date">
              <h4>{day}</h4>
              {dayItems.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')).map(item => (
                <div key={item.id} className="travel-itinerary-item">
                  {editingItem?.id === item.id && editingItem?._section === 'itinerary' ? (
                    <form onSubmit={e => handleUpdateItem(e, 'itinerary', item.id)} style={{ flex: 1 }}>
                      <div className="travel-form-row">
                        <input value={editingItem.title || ''} onChange={e => setEditingItem({ ...editingItem, title: e.target.value })} required />
                        <select value={editingItem.category ?? 0} onChange={e => setEditingItem({ ...editingItem, category: parseInt(e.target.value) })}>
                          {Object.entries(itineraryCategoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </div>
                      <div className="travel-form-row">
                        <input type="date" value={editingItem.date || ''} onChange={e => setEditingItem({ ...editingItem, date: e.target.value })} />
                        <input type="time" value={editingItem.startTime || ''} onChange={e => setEditingItem({ ...editingItem, startTime: e.target.value })} />
                        <input type="time" value={editingItem.endTime || ''} onChange={e => setEditingItem({ ...editingItem, endTime: e.target.value })} />
                      </div>
                      <div className="travel-form-actions">
                        <button type="submit" className="travel-btn primary small">{t('travel.common.save')}</button>
                        <button type="button" className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="travel-itinerary-time">{item.startTime || '—'}</div>
                      <div style={{ flex: 1 }}>
                        <div><strong>{item.title}</strong> <span className="travel-type-badge">{itineraryCategoryLabels[item.category] || 'Other'}</span></div>
                        {item.location && <div className="travel-item-detail">📍 {item.location}</div>}
                        {item.description && <div className="travel-item-detail">{item.description}</div>}
                      </div>
                      <div className="travel-item-actions">
                        <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'itinerary' })}>✏️</button>
                        <button className="travel-btn danger small" onClick={() => handleDeleteItem('itinerary', item.id)}>🗑️</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    );
  };

  // --- Render: Reservations Tab ---
  const renderReservations = () => {
    const items = unwrap(selectedTrip.reservations);
    return (
      <div className="travel-section">
        <div className="travel-section-header">
          <h3>🎫 {t('travel.reservations.title')}</h3>
          <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'reservations', type: 0 })}>+ {t('travel.common.add')}</button>
        </div>
        {newItem._adding === 'reservations' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'reservations')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.reservations.namePlaceholder')} value={newItem.name || ''} onChange={e => setNewItem({ ...newItem, name: e.target.value })} required />
              <select value={newItem.type ?? 0} onChange={e => setNewItem({ ...newItem, type: parseInt(e.target.value) })}>
                {Object.entries(reservationTypeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="travel-form-row">
              <input type="datetime-local" value={newItem.dateTime || ''} onChange={e => setNewItem({ ...newItem, dateTime: e.target.value })} required />
              <input placeholder={t('travel.accommodations.confirmation')} value={newItem.confirmationNumber || ''} onChange={e => setNewItem({ ...newItem, confirmationNumber: e.target.value })} />
            </div>
            <div className="travel-form-row">
              <input placeholder={t('travel.itinerary.location')} value={newItem.location || ''} onChange={e => setNewItem({ ...newItem, location: e.target.value })} />
              <textarea placeholder={t('travel.common.notes')} value={newItem.notes || ''} onChange={e => setNewItem({ ...newItem, notes: e.target.value })} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}
        {items.length === 0 ? <div className="travel-empty">{t('travel.reservations.noReservations')}</div> : items.map(item => (
          <div key={item.id} className="travel-item-card">
            {editingItem?.id === item.id && editingItem?._section === 'reservations' ? (
              <form onSubmit={e => handleUpdateItem(e, 'reservations', item.id)}>
                <div className="travel-form-row">
                  <input value={editingItem.name || ''} onChange={e => setEditingItem({ ...editingItem, name: e.target.value })} required />
                  <select value={editingItem.type ?? 0} onChange={e => setEditingItem({ ...editingItem, type: parseInt(e.target.value) })}>
                    {Object.entries(reservationTypeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="travel-form-row">
                  <input type="datetime-local" value={editingItem.dateTime || ''} onChange={e => setEditingItem({ ...editingItem, dateTime: e.target.value })} />
                  <input value={editingItem.confirmationNumber || ''} onChange={e => setEditingItem({ ...editingItem, confirmationNumber: e.target.value })} placeholder={t('travel.accommodations.confirmation')} />
                </div>
                <div className="travel-form-actions">
                  <button type="submit" className="travel-btn primary small">{t('travel.common.save')}</button>
                  <button type="button" className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
                </div>
              </form>
            ) : (
              <>
                <div className="travel-item-header">
                  <div>
                    <h4>{item.name} <span className="travel-type-badge">{reservationTypeLabels[item.type] ?? 'Other'}</span></h4>
                  </div>
                  <div className="travel-item-actions">
                    <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'reservations' })}>✏️</button>
                    <button className="travel-btn danger small" onClick={() => handleDeleteItem('reservations', item.id)}>🗑️</button>
                  </div>
                </div>
                <div className="travel-item-detail">📅 {formatDateTime(item.dateTime)}</div>
                {item.location && <div className="travel-item-detail">📍 {item.location}</div>}
                {item.confirmationNumber && <div className="travel-item-detail">{t('travel.reservations.confirmationLabel')} {item.confirmationNumber}</div>}
                {item.notes && <div className="travel-item-detail">{item.notes}</div>}
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // --- Render: Budget Tab ---
  const renderBudget = () => {
    const items = unwrap(selectedTrip.budgetItems);
    const totalEstimated = items.reduce((s, b) => s + (b.estimatedAmount || 0), 0);
    const totalActual = items.reduce((s, b) => s + (b.actualAmount || 0), 0);

    // Costs already committed elsewhere in the trip (flights, stays, reservations).
    const bookedLines = [
      ...unwrap(selectedTrip.flights).map(f => ({
        key: `flight-${f.id}`,
        icon: '✈️',
        label: [f.airline, f.flightNumber].filter(Boolean).join(' ') || t('travel.budget.flight'),
        cost: f.cost,
        currency: f.currency
      })),
      ...unwrap(selectedTrip.accommodations).map(a => ({
        key: `stay-${a.id}`,
        icon: '🏨',
        label: a.name,
        cost: a.cost,
        currency: a.currency
      })),
      ...unwrap(selectedTrip.reservations).map(r => ({
        key: `res-${r.id}`,
        icon: '🎟️',
        label: r.name,
        cost: r.cost,
        currency: r.currency
      }))
    ].filter(l => (l.cost || 0) > 0);

    // Never sum across currencies — show each total separately.
    const bookedByCurrency = bookedLines.reduce((acc, l) => {
      const code = (l.currency || 'NOK').toUpperCase();
      acc[code] = (acc[code] || 0) + l.cost;
      return acc;
    }, {});
    const bookedSummary = Object.entries(bookedByCurrency)
      .map(([code, value]) => `${Math.round(value).toLocaleString('nb-NO')} ${code}`)
      .join(' · ');

    return (
      <div className="travel-section">
        <div className="travel-section-header">
          <h3>💰 {t('travel.budget.title')}</h3>
          <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'budget' })}>+ {t('travel.common.add')}</button>
        </div>
        <div className="travel-budget-summary">
          <div className="budget-stat"><div className="budget-stat-value">{totalEstimated.toLocaleString()}</div><div className="budget-stat-label">{t('travel.budget.estimated')}</div></div>
          <div className="budget-stat"><div className="budget-stat-value">{bookedSummary || '—'}</div><div className="budget-stat-label">{t('travel.budget.booked')}</div></div>
          <div className="budget-stat"><div className="budget-stat-value">{totalActual.toLocaleString()}</div><div className="budget-stat-label">{t('travel.budget.actual')}</div></div>
          <div className="budget-stat"><div className="budget-stat-value" style={{ color: totalActual > totalEstimated ? 'var(--danger-600)' : 'var(--success-600)' }}>{(totalEstimated - totalActual).toLocaleString()}</div><div className="budget-stat-label">{t('travel.budget.remaining')}</div></div>
        </div>

        {bookedLines.length > 0 && (
          <div className="travel-booked">
            <h4 className="travel-booked-title">{t('travel.budget.bookedTitle')}</h4>
            <ul className="travel-booked-list">
              {bookedLines.map(l => (
                <li key={l.key} className="travel-booked-row">
                  <span className="travel-booked-icon">{l.icon}</span>
                  <span className="travel-booked-label">{l.label}</span>
                  <span className="travel-booked-cost">
                    {Math.round(l.cost).toLocaleString('nb-NO')} {(l.currency || 'NOK').toUpperCase()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {newItem._adding === 'budget' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'budget')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.budget.categoryPlaceholder')} value={newItem.category || ''} onChange={e => setNewItem({ ...newItem, category: e.target.value })} required />
              <input type="number" step="0.01" placeholder={t('travel.budget.estimated')} value={newItem.estimatedAmount || ''} onChange={e => setNewItem({ ...newItem, estimatedAmount: parseFloat(e.target.value) || 0 })} />
              <input type="number" step="0.01" placeholder={t('travel.budget.actual')} value={newItem.actualAmount || ''} onChange={e => setNewItem({ ...newItem, actualAmount: parseFloat(e.target.value) || 0 })} />
            </div>
            <div className="travel-form-row">
              <textarea placeholder={t('travel.common.notes')} value={newItem.notes || ''} onChange={e => setNewItem({ ...newItem, notes: e.target.value })} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}
        {items.length === 0 ? <div className="travel-empty">{t('travel.budget.noBudget')}</div> : (
          <table className="travel-budget-table">
            <thead>
              <tr><th>{t('travel.budget.category')}</th><th>{t('travel.budget.estimated')}</th><th>{t('travel.budget.actual')}</th><th>{t('travel.budget.diff')}</th><th></th></tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  {editingItem?.id === item.id && editingItem?._section === 'budget' ? (
                    <>
                      <td><input value={editingItem.category || ''} onChange={e => setEditingItem({ ...editingItem, category: e.target.value })} style={{ width: '100%' }} /></td>
                      <td><input type="number" step="0.01" value={editingItem.estimatedAmount || ''} onChange={e => setEditingItem({ ...editingItem, estimatedAmount: parseFloat(e.target.value) || 0 })} style={{ width: '80px' }} /></td>
                      <td><input type="number" step="0.01" value={editingItem.actualAmount || ''} onChange={e => setEditingItem({ ...editingItem, actualAmount: parseFloat(e.target.value) || 0 })} style={{ width: '80px' }} /></td>
                      <td colSpan="2">
                        <button className="travel-btn primary small" onClick={e => handleUpdateItem(e, 'budget', item.id)}>{t('travel.common.save')}</button>
                        <button className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{item.category}</td>
                      <td>{(item.estimatedAmount || 0).toLocaleString()}</td>
                      <td>{(item.actualAmount || 0).toLocaleString()}</td>
                      <td style={{ color: (item.actualAmount || 0) > (item.estimatedAmount || 0) ? 'var(--danger-600)' : 'var(--success-600)' }}>
                        {((item.estimatedAmount || 0) - (item.actualAmount || 0)).toLocaleString()}
                      </td>
                      <td>
                        <div className="travel-item-actions">
                          <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'budget' })}>✏️</button>
                          <button className="travel-btn danger small" onClick={() => handleDeleteItem('budget', item.id)}>🗑️</button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              <tr className="total-row">
                <td><strong>{t('travel.budget.total')}</strong></td>
                <td><strong>{totalEstimated.toLocaleString()}</strong></td>
                <td><strong>{totalActual.toLocaleString()}</strong></td>
                <td style={{ color: totalActual > totalEstimated ? 'var(--danger-600)' : 'var(--success-600)' }}><strong>{(totalEstimated - totalActual).toLocaleString()}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    );
  };

  // --- Render: Documents Tab ---
  const renderDocuments = () => {
    const items = unwrap(selectedTrip.documents);
    return (
      <div className="travel-section">
        <div className="travel-section-header">
          <h3>📄 {t('travel.documents.title')}</h3>
          <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'documents', type: 0 })}>+ {t('travel.common.add')}</button>
        </div>
        {newItem._adding === 'documents' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'documents')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.documents.titleField')} value={newItem.title || ''} onChange={e => setNewItem({ ...newItem, title: e.target.value })} required />
              <select value={newItem.type ?? 0} onChange={e => setNewItem({ ...newItem, type: parseInt(e.target.value) })}>
                {Object.entries(documentTypeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="travel-form-row">
              <input placeholder={t('travel.documents.documentNumber')} value={newItem.documentNumber || ''} onChange={e => setNewItem({ ...newItem, documentNumber: e.target.value })} />
              <input type="date" placeholder={t('travel.documents.expiry')} value={newItem.expiryDate || ''} onChange={e => setNewItem({ ...newItem, expiryDate: e.target.value })} />
            </div>
            <div className="travel-form-row">
              <textarea placeholder={t('travel.common.notes')} value={newItem.notes || ''} onChange={e => setNewItem({ ...newItem, notes: e.target.value })} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}
        {items.length === 0 ? <div className="travel-empty">{t('travel.documents.noDocuments')}</div> : items.map(item => (
          <div key={item.id} className="travel-item-card">
            {editingItem?.id === item.id && editingItem?._section === 'documents' ? (
              <form onSubmit={e => handleUpdateItem(e, 'documents', item.id)}>
                <div className="travel-form-row">
                  <input value={editingItem.title || ''} onChange={e => setEditingItem({ ...editingItem, title: e.target.value })} required />
                  <select value={editingItem.type ?? 0} onChange={e => setEditingItem({ ...editingItem, type: parseInt(e.target.value) })}>
                    {Object.entries(documentTypeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="travel-form-row">
                  <input value={editingItem.documentNumber || ''} onChange={e => setEditingItem({ ...editingItem, documentNumber: e.target.value })} placeholder={t('travel.documents.documentNumber')} />
                  <input type="date" value={editingItem.expiryDate || ''} onChange={e => setEditingItem({ ...editingItem, expiryDate: e.target.value })} />
                </div>
                <div className="travel-form-actions">
                  <button type="submit" className="travel-btn primary small">{t('travel.common.save')}</button>
                  <button type="button" className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
                </div>
              </form>
            ) : (
              <>
                <div className="travel-item-header">
                  <div>
                    <h4>{item.title} <span className="travel-type-badge">{documentTypeLabels[item.type] ?? 'Other'}</span></h4>
                  </div>
                  <div className="travel-item-actions">
                    <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'documents' })}>✏️</button>
                    <button className="travel-btn danger small" onClick={() => handleDeleteItem('documents', item.id)}>🗑️</button>
                  </div>
                </div>
                {item.documentNumber && <div className="travel-item-detail">{t('travel.documents.numberLabel')} {item.documentNumber}</div>}
                {item.expiryDate && <div className="travel-item-detail">{t('travel.documents.expiresLabel')} {formatDate(item.expiryDate)}</div>}
                {item.notes && <div className="travel-item-detail">{item.notes}</div>}
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // --- Render: Contacts Tab ---
  const renderContacts = () => {
    const items = unwrap(selectedTrip.contacts);
    return (
      <div className="travel-section">
        <div className="travel-section-header">
          <h3>📞 {t('travel.contacts.title')}</h3>
          <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'contacts', category: 0 })}>+ {t('travel.common.add')}</button>
        </div>
        {newItem._adding === 'contacts' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'contacts')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.contacts.name')} value={newItem.name || ''} onChange={e => setNewItem({ ...newItem, name: e.target.value })} required />
              <select value={newItem.category ?? 0} onChange={e => setNewItem({ ...newItem, category: parseInt(e.target.value) })}>
                {Object.entries(contactCategoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="travel-form-row">
              <input placeholder={t('travel.contacts.phone')} value={newItem.phone || ''} onChange={e => setNewItem({ ...newItem, phone: e.target.value })} />
              <input placeholder={t('travel.contacts.email')} type="email" value={newItem.email || ''} onChange={e => setNewItem({ ...newItem, email: e.target.value })} />
            </div>
            <div className="travel-form-row">
              <textarea placeholder={t('travel.common.notes')} value={newItem.notes || ''} onChange={e => setNewItem({ ...newItem, notes: e.target.value })} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}
        {items.length === 0 ? <div className="travel-empty">{t('travel.contacts.noContacts')}</div> : items.map(item => (
          <div key={item.id} className="travel-item-card">
            {editingItem?.id === item.id && editingItem?._section === 'contacts' ? (
              <form onSubmit={e => handleUpdateItem(e, 'contacts', item.id)}>
                <div className="travel-form-row">
                  <input value={editingItem.name || ''} onChange={e => setEditingItem({ ...editingItem, name: e.target.value })} required />
                  <select value={editingItem.category ?? 0} onChange={e => setEditingItem({ ...editingItem, category: parseInt(e.target.value) })}>
                    {Object.entries(contactCategoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="travel-form-row">
                  <input value={editingItem.phone || ''} onChange={e => setEditingItem({ ...editingItem, phone: e.target.value })} placeholder={t('travel.contacts.phone')} />
                  <input value={editingItem.email || ''} onChange={e => setEditingItem({ ...editingItem, email: e.target.value })} placeholder={t('travel.contacts.email')} />
                </div>
                <div className="travel-form-actions">
                  <button type="submit" className="travel-btn primary small">{t('travel.common.save')}</button>
                  <button type="button" className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
                </div>
              </form>
            ) : (
              <>
                <div className="travel-item-header">
                  <div>
                    <h4>{item.name} <span className="travel-type-badge">{contactCategoryLabels[item.category] ?? 'Other'}</span></h4>
                  </div>
                  <div className="travel-item-actions">
                    <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'contacts' })}>✏️</button>
                    <button className="travel-btn danger small" onClick={() => handleDeleteItem('contacts', item.id)}>🗑️</button>
                  </div>
                </div>
                {item.phone && <div className="travel-item-detail">📞 {item.phone}</div>}
                {item.email && <div className="travel-item-detail">✉️ {item.email}</div>}
                {item.notes && <div className="travel-item-detail">{item.notes}</div>}
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // --- Render: Notes Tab ---
  const renderNotes = () => {
    const items = unwrap(selectedTrip.notes);
    return (
      <div className="travel-section">
        <div className="travel-section-header">
          <h3>📝 {t('travel.notes.title')}</h3>
          <button className="travel-btn primary small" onClick={() => setNewItem({ _adding: 'notes', category: 5 })}>+ {t('travel.common.add')}</button>
        </div>
        {newItem._adding === 'notes' && (
          <form className="travel-form" onSubmit={e => handleAddItem(e, 'notes')}>
            <div className="travel-form-row">
              <input placeholder={t('travel.documents.titleField')} value={newItem.title || ''} onChange={e => setNewItem({ ...newItem, title: e.target.value })} required />
              <select value={newItem.category ?? 5} onChange={e => setNewItem({ ...newItem, category: parseInt(e.target.value) })}>
                {Object.entries(noteCategoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="travel-form-row">
              <textarea placeholder={t('travel.notes.content')} value={newItem.content || ''} onChange={e => setNewItem({ ...newItem, content: e.target.value })} required rows={3} />
            </div>
            <div className="travel-form-actions">
              <button type="submit" className="travel-btn primary small">{t('travel.common.add')}</button>
              <button type="button" className="travel-btn secondary small" onClick={() => setNewItem({})}>{t('travel.common.cancel')}</button>
            </div>
          </form>
        )}
        {items.length === 0 ? <div className="travel-empty">{t('travel.notes.noNotes')}</div> : items.map(item => (
          <div key={item.id} className="travel-item-card">
            {editingItem?.id === item.id && editingItem?._section === 'notes' ? (
              <form onSubmit={e => handleUpdateItem(e, 'notes', item.id)}>
                <div className="travel-form-row">
                  <input value={editingItem.title || ''} onChange={e => setEditingItem({ ...editingItem, title: e.target.value })} required />
                  <select value={editingItem.category ?? 5} onChange={e => setEditingItem({ ...editingItem, category: parseInt(e.target.value) })}>
                    {Object.entries(noteCategoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="travel-form-row">
                  <textarea value={editingItem.content || ''} onChange={e => setEditingItem({ ...editingItem, content: e.target.value })} required rows={3} />
                </div>
                <div className="travel-form-actions">
                  <button type="submit" className="travel-btn primary small">{t('travel.common.save')}</button>
                  <button type="button" className="travel-btn secondary small" onClick={() => setEditingItem(null)}>{t('travel.common.cancel')}</button>
                </div>
              </form>
            ) : (
              <>
                <div className="travel-item-header">
                  <div>
                    <h4>{item.title} <span className="travel-type-badge">{noteCategoryLabels[item.category] ?? 'General'}</span></h4>
                  </div>
                  <div className="travel-item-actions">
                    <button className="travel-btn secondary small" onClick={() => setEditingItem({ ...item, _section: 'notes' })}>✏️</button>
                    <button className="travel-btn danger small" onClick={() => handleDeleteItem('notes', item.id)}>🗑️</button>
                  </div>
                </div>
                <div className="travel-item-detail">{item.content}</div>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // --- Render: Tab Content Router ---
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'todos': return renderTodos();
      case 'packing': return renderPacking();
      case 'flights': return renderFlights();
      case 'accommodations': return renderAccommodations();
      case 'itinerary': return renderItinerary();
      case 'reservations': return renderReservations();
      case 'budget': return renderBudget();
      case 'documents': return renderDocuments();
      case 'contacts': return renderContacts();
      case 'notes': return renderNotes();
      default: return renderOverview();
    }
  };

  // --- Render: Trip Detail ---
  const renderTripDetail = () => (
    <>
      <div className="travel-detail-header">
        <button className="travel-btn secondary" onClick={() => { setSelectedTrip(null); setEditingItem(null); setNewItem({}); }}>{t('travel.back')}</button>
        <h2>{selectedTrip.name}</h2>
        <span className={`travel-status-badge ${tripStatusClasses[selectedTrip.status] || 'planning'}`}>
          {tripStatusLabels[selectedTrip.status] || 'Planning'}
        </span>
        <button className="travel-btn danger small" onClick={() => handleDeleteTrip(selectedTrip.id)}>{t('travel.deleteTrip')}</button>
      </div>
      <div className="travel-tabs" role="tablist" aria-label="Trip sections">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`travel-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setEditingItem(null); setNewItem({}); }}
            role="tab"
            aria-selected={activeTab === tab.id}
          >
            <span className="tab-icon">{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>
      {renderTabContent()}
    </>
  );

  // --- Main Render ---
  if (loading && !selectedTrip) {
    return <div className="travel-app"><AppHeader title={'✈️ ' + t('travel.title')} user={user} /><p>{t('travel.loadingTrips')}</p></div>;
  }

  return (
    <div className="travel-app">
      <AppHeader title={'✈️ ' + t('travel.title')} user={user} />
      {message && <div className={`travel-message ${message.type}`} role="status" aria-live="polite">{message.text}</div>}
      {error && <div className="travel-message error" role="alert">{error}</div>}
      {selectedTrip ? renderTripDetail() : renderTripList()}
    </div>
  );
}

export default TravelApp;
